import { LogType } from '@model/common/logging';
import { BasketActions } from '@state/basket';
import { Logging, LoggingParams } from '@util/logging';
import { LogCategory } from '@model/common/logging/log-category';
import { FromPricePackagesActions } from '@state/search-results';
import { fetchTours } from '@state/slices/search-results/tours';
import { createListener } from '@state/store/createListener';

export interface LogParams extends LoggingParams {
  type: LogType;
}

export interface LogItem {
  [key: string]: LogParams;
}

export const actionLogMapping: LogItem = {
  [BasketActions.RECEIVE_BASKET_SUCCESS]: {
    text: 'Basket update successful',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.RECEIVE_BASKET_FAILURE]: {
    text: 'Basket update failed',
    category: LogCategory.BASKET,
    type: LogType.WARN
  },
  [BasketActions.RECEIVE_ADD_TO_BASKET_FAILURE]: {
    text: 'Basket update failed',
    category: LogCategory.BASKET,
    type: LogType.WARN
  },
  [FromPricePackagesActions.PERFORM_FROM_PRICE_SEARCH]: {
    text: 'Performing from price search',
    category: LogCategory.FROM_PRICE_PACKAGES,
    type: LogType.INFO
  },
  [FromPricePackagesActions.RECEIVE_FROM_PRICE_PACKAGES_FAILURE]: {
    text: 'Fetching from price packages failed',
    category: LogCategory.FROM_PRICE_PACKAGES,
    type: LogType.WARN
  },
  [FromPricePackagesActions.RECEIVE_FROM_PRICE_PACKAGES_SUCCESS]: {
    text: 'Fetched from price packages successfully',
    category: LogCategory.FROM_PRICE_PACKAGES,
    type: LogType.INFO
  },
  [fetchTours.pending.type]: {
    text: 'Performing tours search',
    category: LogCategory.TOURS,
    type: LogType.INFO
  },
  [fetchTours.fulfilled.type]: {
    text: 'Fetched tours successfully',
    category: LogCategory.TOURS,
    type: LogType.INFO
  },
  [fetchTours.rejected.type]: {
    text: 'Fetching tours failed',
    category: LogCategory.TOURS,
    type: LogType.WARN
  },
  [BasketActions.ADD_ROOMS_TO_BASKET]: {
    text: 'Rooms added to basket',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.ADD_ROOMS_TO_BASKET_AND_NAVIGATE]: {
    text: 'Rooms added to basket & navigation performed',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.ADD_FLIGHT_TO_BASKET]: {
    text: 'Flight added to basket',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.ADD_LUGGAGE_TO_BASKET]: {
    text: 'Luggage added to basket',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.ADD_TRANSFERS_TO_BASKET]: {
    text: 'Transfer added to basket',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.ADD_SPECIAL_REQUESTS_TO_BASKET]: {
    text: 'Special request added to basket',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.ADD_SUPPLEMENTS_TO_BASKET]: {
    text: 'Supplements added to basket',
    category: LogCategory.BASKET,
    type: LogType.INFO
  },
  [BasketActions.PREBOOK_FLIGHT_TO_BASKET]: {
    text: 'Flight PreBook performed',
    category: LogCategory.BASKET,
    type: LogType.INFO
  }
};

export const performLogging = async (logParam: LogParams, data: any) => {
  switch (logParam.type) {
    case LogType.ERROR:
      Logging.error({ ...logParam, data });
      break;
    case LogType.WARN:
      Logging.warn({ ...logParam, data });
      break;
    case LogType.LOG:
      Logging.log({ ...logParam, data });
      break;
    default:
      // Default to standard logging if type is not recognized
      Logging.log({ ...logParam, data });
      break;
  }
};

export const createActionCreator = (actionType: string) => () => ({ type: actionType });

const loggableActionCreators = [
  createActionCreator(BasketActions.RECEIVE_BASKET_SUCCESS),
  createActionCreator(BasketActions.RECEIVE_BASKET_FAILURE),
  createActionCreator(BasketActions.RECEIVE_ADD_TO_BASKET_FAILURE),
  createActionCreator(FromPricePackagesActions.PERFORM_FROM_PRICE_SEARCH),
  createActionCreator(FromPricePackagesActions.RECEIVE_FROM_PRICE_PACKAGES_FAILURE),
  createActionCreator(FromPricePackagesActions.RECEIVE_FROM_PRICE_PACKAGES_SUCCESS),
  createActionCreator(fetchTours.pending.type),
  createActionCreator(fetchTours.fulfilled.type),
  createActionCreator(fetchTours.rejected.type),
  createActionCreator(BasketActions.ADD_ROOMS_TO_BASKET),
  createActionCreator(BasketActions.ADD_ROOMS_TO_BASKET_AND_NAVIGATE),
  createActionCreator(BasketActions.ADD_FLIGHT_TO_BASKET),
  createActionCreator(BasketActions.ADD_LUGGAGE_TO_BASKET),
  createActionCreator(BasketActions.ADD_TRANSFERS_TO_BASKET),
  createActionCreator(BasketActions.ADD_SPECIAL_REQUESTS_TO_BASKET),
  createActionCreator(BasketActions.ADD_SUPPLEMENTS_TO_BASKET),
  createActionCreator(BasketActions.PREBOOK_FLIGHT_TO_BASKET)
];

/* *************** *
 *   LISTENERS     *
 * *************** */
export const setupLoggingListeners = () => {
  createListener(loggableActionCreators, async (action) => {
    const logParam: LogParams = actionLogMapping[action.type];

    if (!logParam) {
      console.warn(`No log configuration found for action: ${action.type}`);
      return;
    }

    try {
      await performLogging(logParam, action.payload);
    } catch (error) {
      console.error(`Failed to log action ${action.type}:`, error);
    }
  });
};
